<script setup lang="ts">
import ProductRemoveFromCart from "@features/shopping/components/ProductRemoveFromCart.vue";
import { useCart } from "@features/shopping/composables";
import { formatPrice } from "@features/shopping/utils/format-price";
import { slugify } from "@shared/utils/slugify";
import { buttonVariants } from "@ui/button";
import { ScrollArea } from "@ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@ui/sheet";
import { watch } from "vue";
import { useRoute } from "vue-router";

const { cartItems, closeCart, hasItems, isCartOpen, subTotal, toggleCart } = useCart();
const route = useRoute("/(product)/cart");

// Close the cart when the route changes
watch(
  () => route.path,
  () => {
    if (route.path === "/cart") closeCart();
  },
);
</script>

<template>
  <!-- Disable the cart if the route is cart -->
  <Sheet :open="isCartOpen && route.path !== '/cart'" @update:open="toggleCart">
    <SheetTrigger as-child>
      <div class="group relative cursor-pointer">
        <img
          src="@assets/images/icons/icon-cart.webp"
          width="20"
          height="20"
          alt="Cart icon"
          class="group-hover:hidden"
        />
        <img
          src="@assets/images/icons/icon-cart-hover.webp"
          width="20"
          height="20"
          alt="Cart icon"
          class="hidden group-hover:block"
        />
        <span
          v-if="hasItems"
          class="cart-count absolute -right-2 -top-2 size-4 rounded-full bg-foreground text-center text-background"
        >
          {{ cartItems.reduce((total, item) => total + item.quantity, 0) }}
        </span>
      </div>
    </SheetTrigger>
    <SheetContent side="right" class="flex w-[320px] flex-col px-0">
      <p class="mt-0 px-4 text-center text-content">Cart</p>
      <p v-if="!hasItems" class="mt-0 px-4 text-center text-content">No products in the cart.</p>
      <template v-else>
        <div class="flex h-full flex-col justify-between gap-4">
          <ScrollArea class="px-4">
            <div
              v-for="item in cartItems"
              :key="`${item.product_sku_id}-${item.sku_addition_param}`"
              class="mb-4 flex justify-between gap-4"
            >
              <div class="relative flex gap-4 overflow-hidden">
                <img
                  v-show="item.product_image"
                  :src="item.product_image || ''"
                  :alt="item.product_name"
                  class="aspect-square size-[60px] object-cover"
                />
                <div class="flex flex-col gap-2">
                  <RouterLink
                    :to="`/product/${slugify(item.product_name)}`"
                    class="-mt-1 line-clamp-2 text-label hover:text-muted-foreground"
                  >
                    {{ item.product_name }}
                  </RouterLink>
                  <p class="text-quantity mt-0 text-muted-foreground">
                    {{ item.quantity }}
                    <span class="mx-0.5">×</span>
                    {{ formatPrice(item.product_thb_price) }}฿
                  </p>
                </div>
              </div>
              <ProductRemoveFromCart :item="item" />
            </div>
          </ScrollArea>
          <div class="sticky bottom-0 flex w-full flex-col gap-4 bg-background">
            <div class="px-4">
              <div class="border-y border-muted py-2">
                <p class="mt-0 text-center text-content">Subtotal: {{ formatPrice(subTotal) }}฿</p>
              </div>
            </div>
            <div class="flex flex-col gap-2 px-4">
              <RouterLink to="/cart" :class="buttonVariants()">View Cart</RouterLink>
              <RouterLink to="/checkout" :class="buttonVariants()">Checkout</RouterLink>
            </div>
          </div>
        </div>
      </template>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.cart-count {
  font-size: 11px;
  line-height: 17px;
  letter-spacing: -0.5px;
  font-weight: 700;
}

.text-quantity {
  font-size: 11px;
  line-height: 15.8px;
  letter-spacing: 0.5px;
  font-weight: 700;
}
</style>
